import {
    ArrowDownTrayIcon,
    ClipboardDocumentCheckIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    PlusIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

import { formatShortDate } from '@/provider/providerAnalytics.types'

import { BASE } from './_Layout'
import {
    buildAnalyticsCsv,
    CapacityScaleSection,
    ClientOutcomesKpis,
    CoordinatorPerformanceSection,
    OutcomeChartsRow,
    PopulationInsightsSection,
} from './analyticsSections'
import { MOCK_ANALYTICS } from './mockData'
import { useProviderSession } from './previewSession'

function timeAgo(value: string): string {
    const d = new Date(value)
    if (isNaN(d.valueOf())) return ''
    const minutes = Math.floor((Date.now() - d.valueOf()) / 60000)
    if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes} minutes ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    const days = Math.floor(hours / 24)
    if (days < 14) return days === 1 ? 'yesterday' : `${days} days ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function downloadCsv() {
    const csv = buildAnalyticsCsv()
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sunshine-pediatric-analytics.csv'
    a.click()
    URL.revokeObjectURL(url)
}

export default function ProviderDashboard() {
    const session = useProviderSession()
    const data = MOCK_ANALYTICS

    const { org, needsAttention, recentActivity } = data
    const seatsRemaining = Math.max(0, org.seatQuota - org.seatsUsed)
    const utilization = org.seatQuota > 0 ? Math.round((org.seatsUsed / org.seatQuota) * 100) : 0

    const isCoordinator = session?.role === 'coordinator'
    const isAdmin = session?.role === 'admin'
    // Admins and viewers get the analytics-first dashboard; coordinators get the
    // operational dashboard (caseload, what needs attention, quick actions).
    const isOversight = !isCoordinator

    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8'>
                <div>
                    <p className='text-sm text-neutral-500'>Welcome back,</p>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>{org.displayName}</h1>
                </div>
                <div className='flex items-center gap-2'>
                    {isOversight && (
                        <button
                            onClick={downloadCsv}
                            className='inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-sm font-medium rounded-lg transition-colors'
                        >
                            <ArrowDownTrayIcon className='w-4 h-4' />
                            Export
                        </button>
                    )}
                    {isAdmin && (
                        <Link
                            to={`${BASE}/licenses`}
                            className='inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors'
                        >
                            <PlusIcon className='w-4 h-4' />
                            Assign a license
                        </Link>
                    )}
                </div>
            </div>

            {/* Seats card (hero) — admin & viewer */}
            {isOversight && (
                <div className='bg-gradient-to-br from-brand-600 to-brand-700 text-white rounded-2xl p-6 mb-8 shadow-sm'>
                    <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
                        <div>
                            <p className='text-xs uppercase tracking-wider text-white/70'>Seats</p>
                            <div className='flex items-baseline gap-2 mt-1'>
                                <span className='text-4xl font-bold'>{org.seatsUsed}</span>
                                <span className='text-xl text-white/70'>/ {org.seatQuota}</span>
                                <span className='text-sm text-white/80 ml-2'>assigned</span>
                            </div>
                            <p className='text-sm text-white/80 mt-1'>
                                {seatsRemaining} seats remaining. {org.seatsActive} clients have activated their
                                license.
                            </p>
                        </div>
                        <div className='w-full sm:w-64'>
                            <div className='flex justify-between text-xs text-white/80 mb-1'>
                                <span>{utilization}% used</span>
                                <span>{seatsRemaining} free</span>
                            </div>
                            <div className='w-full h-2.5 bg-white/20 rounded-full overflow-hidden'>
                                <div className='h-full bg-white rounded-full' style={{ width: `${utilization}%` }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Client outcomes — every role */}
            <ClientOutcomesKpis />

            {/* Admin & viewer: analytics-first. Charts flow under the outcome KPIs. */}
            {isOversight && (
                <>
                    <OutcomeChartsRow />
                    <CoordinatorPerformanceSection />
                    <CapacityScaleSection />
                </>
            )}

            {/* Coordinator: operational widgets, then the outcome charts at the bottom. */}
            {isCoordinator && (
                <>
                    <NeedsAttention needsAttention={needsAttention} />
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10'>
                        <RecentActivity recentActivity={recentActivity} />
                        <QuickActions />
                    </div>
                    <OutcomeChartsRow />
                </>
            )}

            {/* Population insights — every role, at the bottom. */}
            <PopulationInsightsSection />
        </div>
    )
}

function NeedsAttention({ needsAttention }: { needsAttention: typeof MOCK_ANALYTICS.needsAttention }) {
    const { overdueHomework, stalledInvites } = needsAttention
    return (
        <div className='bg-white rounded-2xl border border-neutral-200 p-6 mb-8'>
            <div className='flex items-center gap-2 mb-4'>
                <ExclamationTriangleIcon className='w-5 h-5 text-amber-500' />
                <h2 className='text-lg font-semibold text-neutral-900'>Needs attention</h2>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Overdue homework */}
                <div>
                    <div className='flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 mb-3'>
                        <ClipboardDocumentCheckIcon className='w-4 h-4' />
                        Overdue homework ({overdueHomework.length})
                    </div>
                    {overdueHomework.length === 0 ? (
                        <p className='text-sm text-neutral-500'>Nothing overdue. Nice.</p>
                    ) : (
                        <ul className='space-y-2'>
                            {overdueHomework.map((h) => (
                                <li key={h.homeworkId}>
                                    <Link
                                        to={`${BASE}/clients/${h.licenseId}`}
                                        className='flex items-start justify-between gap-3 p-3 rounded-lg border border-red-100 bg-red-50/40 hover:border-red-300 transition-colors'
                                    >
                                        <div className='min-w-0'>
                                            <div className='text-sm font-medium text-neutral-900 truncate'>
                                                {h.childName ?? 'Client'}
                                            </div>
                                            <div className='text-xs text-neutral-600 truncate'>{h.title}</div>
                                        </div>
                                        <span className='text-xs font-medium text-red-700 whitespace-nowrap'>
                                            Due {formatShortDate(h.dueDate)}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Stalled invites */}
                <div>
                    <div className='flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 mb-3'>
                        <EnvelopeIcon className='w-4 h-4' />
                        Invites awaiting registration 2+ weeks ({stalledInvites.length})
                    </div>
                    {stalledInvites.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No stalled invitations.</p>
                    ) : (
                        <ul className='space-y-2'>
                            {stalledInvites.map((s) => (
                                <li key={s.licenseId}>
                                    <Link
                                        to={`${BASE}/licenses`}
                                        className='flex items-start justify-between gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50/40 hover:border-amber-300 transition-colors'
                                    >
                                        <div className='min-w-0'>
                                            <div className='text-sm font-medium text-neutral-900 truncate'>
                                                {s.email}
                                            </div>
                                            <div className='text-xs text-neutral-600'>
                                                Consider resending the invitation
                                            </div>
                                        </div>
                                        <span className='text-xs font-medium text-amber-700 whitespace-nowrap'>
                                            Invited {formatShortDate(s.invitedAt)}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}

function RecentActivity({ recentActivity }: { recentActivity: typeof MOCK_ANALYTICS.recentActivity }) {
    return (
        <div className='lg:col-span-2 bg-white rounded-2xl border border-neutral-200 p-6'>
            <div className='flex items-center justify-between mb-4'>
                <h2 className='text-lg font-semibold text-neutral-900'>Recent activity</h2>
                <Link to={`${BASE}/clients`} className='text-sm text-brand-600 hover:underline'>
                    View all clients
                </Link>
            </div>
            {recentActivity.length === 0 ? (
                <p className='text-sm text-neutral-500'>No activity yet. Invite clients to get started.</p>
            ) : (
                <ul className='space-y-3'>
                    {recentActivity.map((a, i) => (
                        <li
                            key={i}
                            className='flex items-start gap-3 pb-3 border-b border-neutral-100 last:border-0 last:pb-0'
                        >
                            <div className='w-2 h-2 rounded-full bg-brand-500 mt-1.5 flex-shrink-0' />
                            <div className='flex-1 min-w-0'>
                                <p className='text-sm text-neutral-900'>{a.text}</p>
                                <p className='text-xs text-neutral-500 mt-0.5'>{timeAgo(a.ts)}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

function QuickActions() {
    return (
        <div className='bg-white rounded-2xl border border-neutral-200 p-6'>
            <h2 className='text-lg font-semibold text-neutral-900 mb-4'>Quick actions</h2>
            <div className='space-y-2'>
                <QuickAction
                    href={`${BASE}/licenses`}
                    label='Assign a license'
                    sub='Send an invitation to a client'
                />
                <QuickAction
                    href={`${BASE}/clients`}
                    label='Review client progress'
                    sub='See engagement signals per client'
                />
                <QuickAction
                    href={`${BASE}/clients`}
                    label='Assign homework'
                    sub='Pick a client, then open their homework'
                />
            </div>
        </div>
    )
}

function QuickAction({ href, label, sub }: { href: string; label: string; sub: string }) {
    return (
        <Link
            to={href}
            className='block p-3 rounded-lg border border-neutral-200 hover:border-brand-400 hover:bg-brand-50/50 transition-all'
        >
            <div className='text-sm font-medium text-neutral-900'>{label}</div>
            <div className='text-xs text-neutral-500'>{sub}</div>
        </Link>
    )
}
